import { Tag, Timeline, Typography } from "antd";
import dayjs from "dayjs";

import {
  describeCollectionsAutomation,
  type CollectionsAutomationSettings,
  COLLECTIONS_CALL_OUTCOME_LABELS,
  COLLECTIONS_CASE_STATUS_LABELS,
  COLLECTIONS_SITUATION_LABELS,
  COLLECTIONS_TASK_TYPE_LABELS,
  CollectionsEventKind,
  type CollectionsCallOutcome,
  type CollectionsCaseStatus,
  type CollectionsEvent,
  type CollectionsSituation,
  type CollectionsTask,
  type CollectionsTaskType,
} from "@fitness-app/data-models/entities/CollectionsCase";

import { eventLabel, formatPln, stepLabel, taskLabel } from "../collectionsLabels";

const SOURCE_LABELS = { system: "system", automation: "automatycznie", user: "ręcznie" } as const;

const describe = (event: CollectionsEvent): string | null => {
  const payload = event.payload as Record<string, any>;
  switch (event.kind) {
    case CollectionsEventKind.InstallmentAdded:
      return `rata ${payload.paymentCycle ?? "?"} · ${formatPln(payload.amount)}`;
    case CollectionsEventKind.PaymentReceived:
      return formatPln(payload.amount);
    case CollectionsEventKind.TaskCompleted: {
      const outcome = payload.outcome
        ? (COLLECTIONS_CALL_OUTCOME_LABELS[payload.outcome as CollectionsCallOutcome] ?? payload.outcome)
        : null;
      return [
        COLLECTIONS_TASK_TYPE_LABELS[payload.taskType as CollectionsTaskType] ?? payload.taskType,
        outcome,
        payload.note,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    case CollectionsEventKind.StatusChanged:
      return `${COLLECTIONS_CASE_STATUS_LABELS[payload.from as CollectionsCaseStatus] ?? payload.from ?? "—"} → ${COLLECTIONS_CASE_STATUS_LABELS[payload.to as CollectionsCaseStatus] ?? payload.to}${payload.note ? ` · ${payload.note}` : ""}`;
    case CollectionsEventKind.SituationChanged:
      return [
        payload.situation ? COLLECTIONS_SITUATION_LABELS[payload.situation as CollectionsSituation] : "wyczyszczona",
        payload.note,
      ]
        .filter(Boolean)
        .join(" · ");
    case CollectionsEventKind.DeclarationSet:
      return `${formatPln(payload.amount)} do ${dayjs(payload.dueAt).format("DD.MM.YYYY")}${payload.note ? ` · ${payload.note}` : ""}`;
    case CollectionsEventKind.SettlementSet:
      return `${payload.installments} × ${formatPln(payload.amount)}${payload.note ? ` · ${payload.note}` : ""}`;
    case CollectionsEventKind.HandedToAgency:
      return [payload.agency, payload.reference, payload.note].filter(Boolean).join(" · ");
    case CollectionsEventKind.CaseOpened:
    case CollectionsEventKind.CaseReopened:
      return `zaległość ${formatPln(payload.overdueAmount)}`;
    case CollectionsEventKind.EmailSent:
    case CollectionsEventKind.SmsSent:
    case CollectionsEventKind.LetterGenerated:
      return [
        stepLabel(payload.step),
        payload.subject ? `„${payload.subject}”` : null,
        payload.status === "failed" ? `nie wysłano: ${payload.error ?? "błąd"}` : null,
      ]
        .filter(Boolean)
        .join(" · ");
    case CollectionsEventKind.AutomationStarted:
      return `${describeCollectionsAutomation(payload.settings as CollectionsAutomationSettings | undefined)} · ${payload.ladder === "card" ? "raty kartą" : "raty przelewem"}`;
    case CollectionsEventKind.DeclarationKept:
    case CollectionsEventKind.DeclarationBroken:
      return `${formatPln(payload.amount)} do ${dayjs(payload.dueAt).format("DD.MM.YYYY")} · wpłacone ${formatPln(payload.recovered ?? 0)}`;
    case CollectionsEventKind.SettlementBroken:
      return `nowa zaległość ${formatPln(payload.amount)}`;
    case CollectionsEventKind.Note:
      return payload.note ?? null;
    default:
      return payload.note ?? null;
  }
};

const colorOf = (kind: CollectionsEvent["kind"]): string => {
  switch (kind) {
    case CollectionsEventKind.PaymentReceived:
    case CollectionsEventKind.DeclarationKept:
      return "green";
    case CollectionsEventKind.InstallmentAdded:
    case CollectionsEventKind.DeclarationBroken:
    case CollectionsEventKind.SettlementBroken:
    case CollectionsEventKind.HandedToAgency:
      return "red";
    case CollectionsEventKind.EmailSent:
    case CollectionsEventKind.SmsSent:
    case CollectionsEventKind.LetterGenerated:
      return "blue";
    default:
      return "gray";
  }
};

interface CaseTimelineProps {
  events: CollectionsEvent[];
  openTasks: CollectionsTask[];
}

/**
 * The brief's colour code: green = done, orange = needs action, gray = future,
 * blue = we are waiting on the client. Open tasks sit on top as what is next.
 */
const CaseTimeline = ({ events, openTasks }: CaseTimelineProps) => {
  const today = dayjs().format("YYYY-MM-DD");

  const items = [
    ...openTasks.map((task) => ({
      color: task.dueAt <= today ? "orange" : "gray",
      children: (
        <div>
          <Typography.Text strong={task.dueAt <= today}>{taskLabel(task.type)}</Typography.Text>
          <Tag className="ml-2" color={task.dueAt <= today ? "orange" : "default"}>
            {task.dueAt <= today ? "do wykonania" : dayjs(task.dueAt).format("DD.MM.YYYY")}
          </Tag>
        </div>
      ),
    })),
    ...events.map((event) => {
      const detail = describe(event);
      return {
        color: colorOf(event.kind),
        children: (
          <div>
            <div className="flex flex-wrap items-baseline gap-2">
              <Typography.Text>{eventLabel(event.kind)}</Typography.Text>
              <Typography.Text type="secondary" className="text-xs">
                {dayjs(event.occurredAt).format("DD.MM.YYYY HH:mm")} · {SOURCE_LABELS[event.source]}
              </Typography.Text>
            </div>
            {detail ? <Typography.Paragraph className="mb-0 text-sm">{detail}</Typography.Paragraph> : null}
          </div>
        ),
      };
    }),
  ];

  return <Timeline items={items} />;
};

export default CaseTimeline;
